// App-icon tile and module tag (APPLE-SPEC §6.2, §7 10.10, CODE-DELTAS A.8). WP-THEME.
// - ModuleDisc (name kept from the first pass): the module's Lucide glyph, white at stroke 1.6, in an app-icon tile — solid --tile (navy;
//   dark #3A74A8), an inner 0.5 px white/.10 edge, no gradient, ring or shadow. Fixed radii by size: 26 → 6, 32 → 8, 38 → 10 (smaller
//   tiles 4–6, larger ones 10). Glyph 15 / 18 / 21 px. Used ONLY in page headers (38), pinned tiles and grouped lists (26–32) and the
//   landing list (26). Decorative by default.
// - ModuleTag: a 13 px OUTLINE glyph + the module's short label, 12/500 in label-2 — never a filled tile — in every snippet footer, meta
//   line and "Draws on" row. The label is always shown.
import type { AnchorHTMLAttributes, CSSProperties, HTMLAttributes } from "react";
import { cx, pick, useEconicLang } from "./lang";
import { econicModule } from "./modules";
import type { EconicLang, EconicModuleKey } from "./types";

export type ModuleDiscSize = 16 | 20 | 22 | 26 | 28 | 30 | 32 | 38 | 44 | 52;

const TILE: Record<ModuleDiscSize, { glyph: number; radius: number }> = {
  16: { glyph: 10, radius: 4 },
  20: { glyph: 12, radius: 4 },
  22: { glyph: 13, radius: 5 },
  26: { glyph: 15, radius: 6 },
  28: { glyph: 16, radius: 6 },
  30: { glyph: 17, radius: 8 },
  32: { glyph: 18, radius: 8 },
  38: { glyph: 21, radius: 10 },
  44: { glyph: 24, radius: 10 },
  52: { glyph: 28, radius: 10 },
};

export interface ModuleDiscProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  module: EconicModuleKey;
  size?: ModuleDiscSize;
  /** When the tile stands alone as the module's only label, give it the module name for assistive technology. */
  labelled?: boolean;
  lang?: EconicLang;
}

export function ModuleDisc({ module, size = 38, labelled = false, lang, className, style, ...rest }: ModuleDiscProps) {
  const l = useEconicLang(lang);
  const m = econicModule(module);
  const t = TILE[size];
  const Icon = m.icon;
  const vars = { "--ec-disc": `${size}px`, "--ec-glyph": `${t.glyph}px`, "--ec-disc-r": `${t.radius}px`, "--ec-stroke": 1.6, ...style } as CSSProperties;
  return (
    <span
      className={cx("ec-mdisc", className)}
      style={vars}
      data-module={m.key}
      {...(labelled ? { role: "img", "aria-label": pick(m.name, l) } : { "aria-hidden": true })}
      {...rest}
    >
      <Icon strokeWidth={1.6} aria-hidden />
    </span>
  );
}

/** The APPLE-SPEC name of ModuleDisc. */
export const AppIconTile = ModuleDisc;

interface ModuleTagOwnProps {
  module: EconicModuleKey;
  /** Kept for compatibility: every module tag is the outline glyph + label now. */
  secondary?: boolean;
  /** Use the full module name instead of the short label. */
  full?: boolean;
  lang?: EconicLang;
}

export type ModuleTagProps = ModuleTagOwnProps &
  ({ href?: undefined } & Omit<HTMLAttributes<HTMLSpanElement>, "children"> | { href: string } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children">);

export function ModuleTag(props: ModuleTagProps) {
  const { module, secondary: _secondary, full = false, lang, className, ...rest } = props;
  void _secondary;
  const l = useEconicLang(lang);
  const m = econicModule(module);
  const label = pick(full ? m.name : m.short, l);
  const Icon = m.icon;
  const inner = (
    <>
      <Icon aria-hidden strokeWidth={1.6} />
      <span>{label}</span>
    </>
  );
  const cls = cx("ec-mod", className);
  if ("href" in rest && typeof rest.href === "string") {
    return (
      <a className={cls} data-module={m.key} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {inner}
      </a>
    );
  }
  return (
    <span className={cls} data-module={m.key} {...(rest as HTMLAttributes<HTMLSpanElement>)}>
      {inner}
    </span>
  );
}
